import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Medal, Zap, Star, User } from 'lucide-react';
import { ListSkeleton } from '../components/Skeletons';
import axios from 'axios';
import { API, avatarUrl } from '../utils/api';

const RANK_CACHE = 'ferraz_ranking_cache';

export const RankingPage = ({ user }) => {
  const [top10, setTop10] = useState(() => {
    try { const c = JSON.parse(localStorage.getItem(RANK_CACHE)); return c?.top10 || []; } catch { return []; }
  });
  const [currentUser, setCurrentUser] = useState(() => {
    try { const c = JSON.parse(localStorage.getItem(RANK_CACHE)); return c?.currentUser || null; } catch { return null; }
  });
  const [loading, setLoading] = useState(top10.length === 0);

  useEffect(() => {
    fetchRanking();
  }, []);

  const fetchRanking = async () => {
    try {
      const response = await axios.get(`${API}/ranking`);
      setTop10(response.data.top_10);
      setCurrentUser(response.data.current_user);
      try { localStorage.setItem(RANK_CACHE, JSON.stringify({ top10: response.data.top_10, currentUser: response.data.current_user })); } catch (e) { console.warn('[RankingPage] non-critical failure', e); }
    } catch (error) {
      console.error('Error fetching ranking:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (index) => {
    if (index === 0) return <Trophy className="w-6 h-6 text-yellow-400" />;
    if (index === 1) return <Medal className="w-6 h-6 text-gray-300" />;
    if (index === 2) return <Medal className="w-6 h-6 text-orange-400" />;
    return (
      <div className="w-8 h-8 flex items-center justify-center text-zinc-500 font-bold">
        {index + 1}
      </div>
    );
  };

  const getRankBgColor = (index) => {
    if (index === 0) return 'from-yellow-900/20 to-yellow-950/10 border-yellow-500/20';
    if (index === 1) return 'from-gray-800/20 to-gray-900/10 border-gray-400/20';
    if (index === 2) return 'from-orange-900/20 to-orange-950/10 border-orange-500/20';
    return 'from-zinc-900/50 to-zinc-950/30 border-white/5';
  };

  return (
    <div className="min-h-screen bg-[#050505] pb-24 px-6 pt-8" data-testid="ranking-page">
      <motion.div
        className="mb-8"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <Zap className="w-8 h-8 text-neon-lime" />
            <h1 className="font-primary font-black text-4xl text-white uppercase tracking-tighter">
              Top 10
            </h1>
          </div>
        </div>
        <p className="text-zinc-500 text-sm">
          Os 10 atletas mais dedicados da equipe
        </p>
      </motion.div>

      {loading ? (
        <ListSkeleton count={5} />
      ) : (
        <>
          {/* Card de Pontuação Individual */}
          {currentUser && (
            <motion.div
              className="mb-6 bg-gradient-to-r from-neon-lime/10 to-neon-purple/10 rounded-2xl p-5 border border-neon-lime/30"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex items-center gap-2 mb-3">
                <User className="w-5 h-5 text-neon-lime" />
                <span className="text-zinc-400 text-sm font-medium">Sua Pontuação</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {currentUser.avatar_v ? (
                    <img
                      src={avatarUrl(currentUser.id, currentUser.avatar_v)}
                      alt={currentUser.name}
                      className="w-14 h-14 rounded-full border-2 border-neon-lime"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-neon-lime to-utility-success flex items-center justify-center text-black font-black text-2xl">
                      {currentUser.name?.[0]}
                    </div>
                  )}
                  <div>
                    <h3 className="font-bold text-white text-lg">{currentUser.name}</h3>
                    <p className="text-zinc-500 text-sm">Nível {currentUser.level}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-primary font-black text-3xl text-neon-lime">
                    {Math.round(currentUser.total_xp || 0)}
                  </div>
                  <div className="text-zinc-500 text-xs uppercase tracking-wider">
                    Pontos
                  </div>
                </div>
              </div>
              {currentUser.in_top_10 && (
                <div className="mt-3 flex items-center gap-2 text-neon-lime text-sm">
                  <Star className="w-4 h-4" />
                  <span className="font-medium">Você está no Top 10!</span>
                </div>
              )}
            </motion.div>
          )}

          {/* Top 10 */}
          <motion.div
            className="space-y-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <h2 className="text-zinc-400 text-sm font-medium uppercase tracking-wider mb-4">
              Ranking Geral
            </h2>
            {top10.length === 0 ? (
              <div className="text-center py-10 text-zinc-500">
                Nenhum atleta no ranking ainda
              </div>
            ) : (
              top10.map((u, index) => {
                const isCurrentUser = u.id === user?.id;
                return (
                  <motion.div
                    key={u.id}
                    className={`bg-gradient-to-r ${getRankBgColor(index)} rounded-2xl p-4 border relative overflow-hidden ${
                      isCurrentUser ? 'ring-2 ring-neon-lime ring-offset-2 ring-offset-[#050505]' : ''
                    }`}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{ scale: 1.02 }}
                    data-testid="rank-item"
                  >
                    {isCurrentUser && (
                      <div className="absolute top-2 right-2">
                        <div className="bg-neon-lime text-black text-xs font-black px-2 py-1 rounded-full">
                          VOCÊ
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-4">
                      <div className="flex-shrink-0">
                        {getRankIcon(index)}
                      </div>
                      <div className="flex items-center gap-3 flex-1">
                        {u.avatar_v ? (
                          <img
                            src={avatarUrl(u.id, u.avatar_v)}
                            loading="lazy"
                            decoding="async"
                            alt={u.name}
                            className="w-12 h-12 rounded-full border-2 border-neon-lime/50"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-neon-lime to-utility-success flex items-center justify-center text-black font-black text-xl">
                            {u.name[0]}
                          </div>
                        )}
                        <div className="flex-1">
                          <h3 className="font-secondary font-bold text-white text-lg">
                            {u.name}
                          </h3>
                          <p className="text-zinc-500 text-sm">Nível {u.level}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-primary font-black text-2xl text-neon-lime">
                          {Math.round(u.total_xp || 0)}
                        </div>
                        <div className="text-zinc-500 text-xs uppercase tracking-wider">
                          Pontos
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })
            )}
          </motion.div>
        </>
      )}
    </div>
  );
};
